import { useProductDetails } from '@app/pdp/hooks';
import type { MappedProductVariant } from '@libs/algolia';
import { getProductById } from '@libs/algolia';
import { clsx, usePrevious } from '@libs/common-next';
import { Button, type IconNames } from '@petsmart-ui/sparky';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

export function CommonFavoriteProductIcon({
    isFavorite,
    handleToggleFavorite,
    callAddToFavorite,
    setIsFavorite,
}: {
    isFavorite: boolean;
    handleToggleFavorite: () => void;
    callAddToFavorite: (favoriteProduct: MappedProductVariant) => void;
    setIsFavorite: (favorite: boolean) => void;
}) {
    const { selectedProduct } = useProductDetails();
    const [urlProductId, setUrlProductId] = useState<string | null>(null);
    const prevUrlProductId = usePrevious(urlProductId);

    const searchParams = useSearchParams();
    const pathname = usePathname();

    useEffect(() => {
        const callToggleFavoriteByURLParam = async () => {
            if (
                urlProductId !== null &&
                urlProductId !== undefined &&
                urlProductId !== prevUrlProductId
            ) {
                const variant = await getProductById(urlProductId);
                const shouldSetIsFavorite =
                    Number(urlProductId) === selectedProduct.id;

                if (!variant) {
                    return;
                }
                callAddToFavorite(variant);
                setIsFavorite(shouldSetIsFavorite);
            }
        };

        callToggleFavoriteByURLParam();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Need to avoid unecessary renders
    }, [searchParams, pathname, urlProductId]);

    useEffect(() => {
        const newSearchParams = new URLSearchParams(searchParams?.toString());

        const productId = newSearchParams.get('wishlist');
        newSearchParams.delete('wishlist');

        if (productId) {
            window.history.replaceState(null, '', `${pathname}`);
            setUrlProductId(productId);
        }
    }, [pathname, searchParams]);

    const componentClassName = clsx([
        'sparky-c-icon-toggle',
        'c-heart-toggle',
        isFavorite && 'sparky-c-icon-toggle--active',
    ]);

    const icon: IconNames = isFavorite ? 'heart-fill' : 'heart-outline';

    return (
        <Button
            className={componentClassName}
            data-testid="test-favorite-product"
            aria-pressed={isFavorite}
            iconName={icon}
            text="Favorite toggle button"
            hideText
            variant="bare"
            iconSize="md"
            onClick={handleToggleFavorite}
        />
    );
}
